import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Record } from '@ltvco/refresh-lib/v1';
import { useContext, useEffect, useCallback, useRef } from 'react';

import { Message } from 'components/ChatBV/types';
import { getPublicRecordsTeaser } from 'components/ChatBV/utils/teaserUtils';

interface UseTeaserLoaderProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

export const useTeaserLoader = ({
  messages,
  setMessages,
}: UseTeaserLoaderProps) => {
  const { logError } = useContext(AppConfig);
  const loadingRef = useRef<Set<string>>(new Set());

  const loadTeaserForMessage = useCallback(
    async (message: Message, index: number) => {
      const messageKey = `teaser-${index}`;

      // Skip if already loading or doesn't meet conditions
      // Also skip court opinion messages - they shouldn't have teasers
      const isCourtOpinionMessage =
        message.content === 'COURT_OPINION_PAGE' ||
        !!message.courtOpinionClusterId;

      if (
        loadingRef.current.has(messageKey) ||
        !message.params ||
        message.searchType !== 'person' ||
        message.isLoadingTeaser !== true ||
        message.teaser ||
        isCourtOpinionMessage // Don't load teaser for court opinion messages
      ) {
        return;
      }

      // Mark as loading
      loadingRef.current.add(messageKey);

      // Helper function to safely update message
      const updateMessage = (updates: Partial<Message>) => {
        setMessages((prevMessages: Message[]) => {
          const updatedMessages = [...prevMessages];
          const currentMessage = updatedMessages[index];
          if (
            currentMessage &&
            currentMessage.params?.first_name === message.params?.first_name &&
            currentMessage.params?.last_name === message.params?.last_name
          ) {
            updatedMessages[index] = {
              ...currentMessage,
              ...updates,
            };
          }
          return updatedMessages;
        });
      };

      try {
        const teaserResult = await getPublicRecordsTeaser({
          first_name: message.params.first_name,
          last_name: message.params.last_name,
          middle_name: message.params.middle_name || '',
          age: message.params.age || '',
          state: message.params.state || '',
          city: message.params.city || '',
        });

        if (teaserResult.teaserData && teaserResult.teaserData.length > 0) {
          updateMessage({
            teaser: teaserResult.teaserData as Record[],
            isLoadingTeaser: false,
          });
        } else {
          updateMessage({ isLoadingTeaser: false });
        }
      } catch (error) {
        logError('Error loading teaser for message', error as Error);
        updateMessage({ isLoadingTeaser: false });
      } finally {
        // Remove from loading set
        loadingRef.current.delete(messageKey);
      }
    },
    [setMessages, logError]
  );

  useEffect(() => {
    // Find all messages that need teaser loading
    messages.forEach((message, index) => {
      // Skip court opinion messages - they shouldn't have teasers
      const isCourtOpinionMessage =
        message.content === 'COURT_OPINION_PAGE' ||
        !!message.courtOpinionClusterId;

      if (
        message.isLoadingTeaser === true &&
        message.params &&
        message.searchType === 'person' &&
        !message.teaser &&
        !isCourtOpinionMessage // Don't load teaser for court opinion messages
      ) {
        loadTeaserForMessage(message, index);
      }
    });
  }, [messages, loadTeaserForMessage]);
};
